import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useHostingAdvancedDomainServicesCheck } from '@newfold/huapi-js';
import {
  AllUserMonarxDetails200RowsItem,
  UserHosting200RowsItem,
} from '@newfold/huapi-js/src/index.schemas';

import HostingResourceCard from '~/components/HostingResourceCard';
import useAddonPurchaseDelayElapsed from '~/hooks/useAddonPurchaseDelayElapsed';
import { useInfoBanners } from '~/hooks/useInfoBanners';
import useResponsive from '~/hooks/useResponsive';
import { useServerStatusToggle } from '~/hooks/useServerStatusToggle';
import { getHostingRenewalStatus } from '~/scenes/Hosting/components/RenewalSettings/utils/helpers';
import AdvancedHostingServerStatus from '~/scenes/Hosting/scenes/HostingDetail/components/AdvancedHosting/components/Server/ServerStatus';
import { isValueEmpty } from '~/utils/common/helpers';
import { YYYY_MMMM_DD_HMS } from '~/utils/date/constants';
import {
  ACCOUNT_TYPES_INVALID_FOR_DOMAIN,
  HOSTING_TYPES_WITHOUT_SERVER_IMAGE_TOOLTIP,
} from '~/utils/hosting/constants';
import {
  getHighestPriorityBanner,
  getSelectedHostingId,
} from '~/utils/hosting/helpers';
import { isFreeTrialAiPlan } from '~/utils/site/helpers';

import {
  calculatePercentage,
  getHostingPlanMonarxInfo,
  getServerStatusandColor,
  isSelfManagedDedicated,
  isSitesAvailable,
  mapPlanToView,
} from '../../utils/helpers';
import PackageDetails from '../PackageDetails';
import Header from './components/Header';
import Metrics from './components/Metric';

interface PackageUsageCardProps {
  hostingPackDetails: UserHosting200RowsItem;
  monarxAddonGroupedList?: Record<string, AllUserMonarxDetails200RowsItem[]>;
}

const PackageUsageCard = ({
  hostingPackDetails,
  monarxAddonGroupedList = {},
}: PackageUsageCardProps) => {
  const { t } = useTranslation('hosting', { keyPrefix: 'hostingNova' });
  const [showDetails, setShowDetails] = useState(false);
  const [serverStatus, setServerStatus] = useState(
    hostingPackDetails?.account_info?.server_status || '',
  );
  const { isMobile, isTablet } = useResponsive();
  const isMobileView = isMobile();
  const isTabletView = isTablet();
  const mappedPlan = mapPlanToView(hostingPackDetails);
  const {
    title,
    status = '',
    atomic = false,
    advanced = false,
    hostingPlanId = '',
    backReference = '',
    expirationDate,
    diskUsed,
    diskUsedPercentage,
    diskTotal,
    phpWorkersUsed,
    phpWorkersTotal,
    websitesUsed,
    websitesTotal,
    details,
    domainName,
    autoRenewFlag = false,
    numberOfSitesAvailable = 0,
    type,
    isPanel,
    isUpgradeable,
    isReseller,
    hasPanelMail,
    featureSet,
    image,
    addonDateAdded,
  } = mappedPlan || {};

  const { dataCenter, ipAddress } = details;
  const {
    os_name = '',
    os_version = '',
    app = '',
    software = '',
  } = image ?? {};
  const appOrSoftware = app || software;
  const displayValue = appOrSoftware
    ? `${appOrSoftware} on ${os_name} ${os_version}`.trim()
    : `${os_name} ${os_version}`.trim();
  const hostingId = getSelectedHostingId(hostingPlanId, backReference);
  const { start: startServer } = useServerStatusToggle({ hostingId });
  const isHosting_01s = featureSet === 'hosting_01s';
  const isSelfManagedVPS = type === 'vps';
  const isSelfManagedDedi = isSelfManagedDedicated(type, title);
  const canShowServerImageTooltip =
    !HOSTING_TYPES_WITHOUT_SERVER_IMAGE_TOOLTIP.has(type ?? '');

  const isAddonAgeThresholdReached = useAddonPurchaseDelayElapsed(
    addonDateAdded,
    status,
    isSelfManagedVPS,
  );
  const isPackageExpired = getHostingRenewalStatus(
    expirationDate ?? '',
    YYYY_MMMM_DD_HMS,
  ).isExpired;

  const hasSitesCapability = isSitesAvailable(
    advanced,
    numberOfSitesAvailable ?? 0,
    isReseller,
  );

  const hasEmailCapability = hasPanelMail || hasPanelMail === null;
  const featureValues: boolean[] = [
    isUpgradeable ?? false,
    isPanel === 'cpanel',
    advanced,
    isPanel === 'cpanel' && advanced,
    hasSitesCapability,
    hasEmailCapability,
  ];
  const isAccountTypeNotSupported = type
    ? !ACCOUNT_TYPES_INVALID_FOR_DOMAIN.has(type)
    : false;
  const hostingPlanMonarxInfo = getHostingPlanMonarxInfo(
    monarxAddonGroupedList,
    backReference,
    domainName,
  );

  const { data: domainServiceData } = useHostingAdvancedDomainServicesCheck(
    hostingId!,
    {
      query: {
        enabled: !!hostingId && !!isAccountTypeNotSupported && advanced,
      },
    },
  );

  const domainData = domainServiceData?.data;
  const domainServiceStatus = {
    canAccessEmail: domainData?.managed_cpanel_user_exists === 1 || !domainData,
    isDomainServiceConfigured: !domainData?.configure_domain_services,
  };

  const canShowWebsitesPieChart = advanced ? hasSitesCapability : true;
  const dataCenterName = atomic ? t('atomicDCName') : dataCenter;
  const canshowIpCard = advanced && !!ipAddress;

  const { color: serverStatusTextColor, status: serverStatusText } =
    getServerStatusandColor(serverStatus);

  const getUsedOfText = (
    used?: string | number | null,
    total?: string | number | null,
  ) => {
    if (isValueEmpty(total) || isValueEmpty(used)) {
      return t('usageUnavailable');
    }
    return (
      <Trans
        t={t}
        i18nKey="usedOf"
        values={{ used, total: total === 'unlimited' ? t('unlimited') : total }}
        components={{ strong: <strong /> }}
      />
    );
  };

  const metrics = [
    {
      label: t('diskUsage.title'),
      displayValue: getUsedOfText(
        diskUsed,
        diskTotal === 'unlimited' ? t('unlimited') : diskTotal,
      ),
      used: diskUsed,
      total: diskTotal,
      percentage: diskUsedPercentage,
      threshold: 80,
      canShow: true,
      isAdvanced: advanced,
    },
    {
      label: t('websites.title'),
      displayValue: getUsedOfText(websitesUsed, websitesTotal),
      used: websitesUsed,
      total: websitesTotal,
      percentage: calculatePercentage(websitesUsed, websitesTotal),
      canShow: canShowWebsitesPieChart,
      isAdvanced: advanced,
    },
    {
      label: t('phpWorkers.title'),
      displayValue: getUsedOfText(phpWorkersUsed, phpWorkersTotal),
      used: phpWorkersUsed,
      total: phpWorkersTotal,
      percentage: calculatePercentage(phpWorkersUsed, phpWorkersTotal),
      canShow: !!atomic,
      isAdvanced: advanced,
    },
    {
      label: t('dataCenter.title'),
      displayValue: dataCenterName,
      canShow: !advanced,
      isAdvanced: advanced,
    },
    {
      label: t('serverStatus.title'),
      displayValue: t(`serverStatusText.${serverStatusText}`),
      displayValueColor: serverStatusTextColor,
      canShow: !!advanced,
      isAdvanced: advanced,
      tooltipMessage: t('serverStatus.tooltip'),
    },
    {
      label: t('ipAddress.title'),
      displayValue: ipAddress,
      showCopyIcon: true,
      canShow: canshowIpCard,
      isAdvanced: advanced,
      tooltipMessage: t('ipAddress.tooltip'),
    },
    {
      label: t('operatingSystem.title'),
      displayValue: displayValue,
      canShow: advanced && !hasSitesCapability,
      isAdvanced: advanced,
      tooltipMessage: canShowServerImageTooltip
        ? t('operatingSystem.tooltipv2')
        : undefined,
    },
  ];

  const handleToggleDetails = () => setShowDetails((prev) => !prev);
  const handleServerStatusChange = (newStatus: string) => {
    setServerStatus(newStatus);
  };

  const isSiteLimitReached =
    websitesTotal !== 'unlimited' && websitesUsed >= Number(websitesTotal);

  const isFreeTrialAiHosting = isFreeTrialAiPlan(
    hostingPackDetails?.billing?.prod_code,
  );

  const banners = useInfoBanners({
    status,
    expirationDate,
    autoRenewFlag,
    diskUsedPercentage,
    isSiteLimitReached,
    onUpgradeClick: () => {},
    canUpgrade: isUpgradeable,
    ctbData: { prodInstId: backReference, productName: title },
    serverStatus,
    startServer,
    isAdvanced: advanced,
    isSelfManagedVPS,
    isSelfManagedDedi,
    isHosting_01s,
    isFreeTrialAiHosting,
    isHostingPlanReady: isAddonAgeThresholdReached,
    monarxDashboardData: hostingPlanMonarxInfo,
    isListPage: true,
  });

  // Show only one banner at a time based on priority: error > warning > info > custom.
  const filteredPriorityBanner = getHighestPriorityBanner(banners);

  return (
    <HostingResourceCard
      banners={filteredPriorityBanner}
      data-testid="plan-usage-card"
      sx={{ borderRadius: '12px' }}
    >
      <CardContent sx={{ flex: 1, pt: 0 }}>
        <Header
          mappedPlan={mappedPlan}
          featureValues={featureValues}
          serverStatus={serverStatus}
          domainServiceStatus={domainServiceStatus}
          isHostingPlanReady={isAddonAgeThresholdReached}
          isPackageExpired={isPackageExpired}
        />
        <Metrics items={metrics} />
        {advanced && status === 'active' && (
          <Stack py={{ xs: 2, md: 3 }}>
            <AdvancedHostingServerStatus
              handleServerStatusChange={handleServerStatusChange}
              planHostingId={hostingId ?? undefined}
              buttonGap={isMobileView || isTabletView ? 2 : 4}
              serverType={type ?? undefined}
            />
          </Stack>
        )}
        {!atomic && (
          <>
            <Collapse in={showDetails} sx={{ mt: showDetails ? 0 : 2 }}>
              {/* Package Details like IPaddress, SSH, DC, FTP etc.. */}
              <PackageDetails
                details={details}
                isAtomic={atomic}
                isAdvanced={advanced}
                osTemplateValue={displayValue}
                hostingPlanId={hostingPlanId || ''}
                backReference={backReference || ''}
                domainServiceStatus={domainServiceStatus}
                serverStatus={serverStatus}
                hasSitesCapability={hasSitesCapability}
                accountType={type}
                serverId={hostingPackDetails?.account_info?.server_id}
              />
            </Collapse>

            <Button
              size="small"
              endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              disabled={status !== 'active'}
              sx={{
                marginTop: 3,
                textTransform: 'none',
                alignContent: 'flex-start',
              }}
              onClick={handleToggleDetails}
            >
              <Typography
                variant="body2"
                fontWeight={500}
                color={status === 'active' ? 'inherit' : 'text.disabled'}
              >
                {showDetails ? t('hideDetails') : t('viewDetails')}
              </Typography>
            </Button>
          </>
        )}
      </CardContent>
    </HostingResourceCard>
  );
};

export default PackageUsageCard;
